import { ContentfulConfigService } from '@scalefast/config-angular';
import type { Asset, Entry } from 'contentful';
import { NoChainModifier, WithoutUnresolvableLinks } from 'src/app/core/interfaces/Contentful/ContentfulChainModifier';
import {
  ContentfulPagePathContent,
  EmptyContentfulPagePathContent,
  ErrorContentfulPagePathContent,
} from 'src/app/core/interfaces/Contentful/PagePath/ContentfulPagePathContent';
import { getPathnameFromPartialURL } from './url.helper';
import { ContentfulCloudinaryImageSkeleton } from 'src/app/core/interfaces/Contentful/CloudinaryImage/ContentfulCloudinaryImageSkeleton';
import { ContentfulCloudinaryImage } from 'src/app/core/interfaces/Contentful/CloudinaryImage/ContentfulCloudinaryImage';

/**
 * Check if an object is a Contentful ResolvedLink.
 * A Contentful ResolvedLink is an object with a fields property.
 *
 * @param {any} obj - The object to check.
 * @returns {boolean} - True if the object is a resolved Contentful Entry, false otherwise.
 */
export function isAContentfulResolvedLink<T extends Entry>(obj: any): obj is T {
  return obj && typeof obj === 'object' && 'fields' in obj;
}

/**
 * Check if an object is a Contentful ResolvedAsset.
 * A Contentful ResolvedAsset is an object with a sys.type property set to 'Asset'.
 *
 * @param {any} obj - The object to check.
 * @returns {boolean} - True if the object is a resolved Contentful Asset, false otherwise.
 */
export function isAContentfulResolvedAsset<T extends Asset>(obj: any): obj is T {
  return obj && typeof obj === 'object' && 'sys' in obj && obj.sys.type == 'Asset';
}

/**
 * Get a fake Contentful Asset object.
 * FIXME: This method is used to mock a Contentful Asset object.
 */
export function getAssetSysFake(): Asset<NoChainModifier> {
  return {
    sys: {
      id: '',
      type: 'Asset',
      createdAt: '2024-09-25T08:00:31Z',
      updatedAt: '2024-09-25T08:00:31Z',
      environment: { sys: { id: 'master', type: 'Link', linkType: 'Environment' } },
      space: { sys: { id: 'spaceId', type: 'Link', linkType: 'Space' } },
      revision: 1,
    },
    metadata: {
      tags: [],
    },
    fields: {
      file: {
        contentType: '',
        fileName: '',
        url: '',
        details: {
          size: 0,
        },
      },
    },
  };
}

export function getContentfulConfig(contentfulConfigService: ContentfulConfigService) {
  const contentfulConfigs = contentfulConfigService.get();
  const contentfulConfig = contentfulConfigs[0];

  if (!contentfulConfig) {
    throw new Error(`No Contentful configuration found in contentful.config.json`);
  }

  return contentfulConfig;
}

export function isAEmptyContentfulPagePathContent(
  contentfulPagePathContent: ContentfulPagePathContent,
): contentfulPagePathContent is EmptyContentfulPagePathContent {
  return contentfulPagePathContent.id === 'empty-content';
}

export function isAnErrorContentfulPagePathContent(
  contentfulPagePathContent: ContentfulPagePathContent,
): contentfulPagePathContent is ErrorContentfulPagePathContent {
  return contentfulPagePathContent.id === 'error';
}

/**
 * Add specific class if current location path is in the array of special sections
 */
export function getClassForContentfulPath(currentUrl: string) {
  const specialSectionContentUrl: Array<{ slug: string; classToAdd: string }> = [
    {
      slug: '/es-es/salud-oral/por-que-oral-b/cepillos-de-dientes-electricos',
      classToAdd: 'text-white description-black',
    },
    { slug: '/es-es/salud-oral/por-que-oral-b', classToAdd: 'content-horizontal-right text-white' },
    {
      slug: '/fr-fr/sante-bucco-dentaire/pourquoi-choisir-oral-b',
      classToAdd: 'content-horizontal-right text-white',
    },
    {
      slug: '/fr-fr/sante-bucco-dentaire/quelle-brosse-a-dents-electrique-choisir-oral-b',
      classToAdd: 'text-white description-black',
    },
    {
      slug: '/fr-fr/produits-dentaires/bienfait-brosse-a-dent-electrique',
      classToAdd: 'text-white content-horizontal-left',
    },
    {
      slug: '/es-es/coleccion-productos/beneficios-cepillos-dientes-electricos',
      classToAdd: 'text-white content-horizontal-left',
    },
    {
      slug: '/coleccion-productos/beneficios-cepillos-dientes-electricos',
      classToAdd: 'text-white content-horizontal-left',
    },
    {
      slug: '/fr-fr/sante-bucco-dentaire/sante-dentaire-par-age',
      classToAdd: 'content-horizontal-right',
    },
    {
      slug: '/fr-fr/sante-bucco-dentaire/quelle-brosse-a-dents-electrique-choisir-oral-b',
      classToAdd: 'text-white',
    },
  ];
  const currentPathWithoutParams = getPathnameFromPartialURL(currentUrl);
  const findSpecialSection = specialSectionContentUrl.find((item) => {
    // Remove the first segment
    const urlWithoutLocale = item.slug.replace(/^\/[^\/]+/, '');

    return item.slug === currentPathWithoutParams || urlWithoutLocale === currentPathWithoutParams;
  });

  return findSpecialSection ? findSpecialSection.classToAdd : '';
}

/**
 * Adapt a ContentfulCloudinaryImageSkeleton to a ContentfulCloudinaryImage.
 */
export function adaptCloudinaryImage(
  cloudinaryImage: Entry<ContentfulCloudinaryImageSkeleton, WithoutUnresolvableLinks, string> | undefined,
): ContentfulCloudinaryImage | undefined {
  if (!cloudinaryImage) return undefined;
  return {
    name: cloudinaryImage.fields.name,
    alternateText: cloudinaryImage.fields.alternateText,
    imageRendition: cloudinaryImage.fields.imageRendition?.fields,
    asset: cloudinaryImage.fields.asset,
  };
}
